import React, { useEffect } from 'react';
import { Stack, Typography, Chip, IconButton, Paper, Box, Button, Divider, Popover } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateForDisplay, registrationCloseExclusive } from "@moment-golf/shared";
import {
    applyEarlyRegDiscount,
    formatDisplayPrice,
    getEarlyRegDiscountAmount,
    getFlightPriceRange,
    getPriceDisplaySuffix,
    isEarlyRegistrationActive,
    scalePriceForDisplay,
    scalePriceRangeForDisplay,
} from "../utils/eventPricingUtils";
import { EarlyRegPriceDisplay } from "./Components/EarlyRegPriceDisplay";
import { useMCContext } from './contexts/useMCStates';
import { UserRegistered } from './Components/UserRegistered';

const QUILTED_FLAG_LOGO = `${process.env.PUBLIC_URL || ''}/Quilted Flag - Bold Zoom Tilt.png`;

interface EventCardSectionProps {
    togglePastEvents: boolean;
    stackRef: React.RefObject<HTMLDivElement | null>;
}

export const EventCardSection = ({togglePastEvents, stackRef }: EventCardSectionProps) => {

    const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null);
    const [popperEventId, setPopperEventId] = useState<string | null>(null);
    const [upcomingOpen, setUpcomingOpen] = useState(true);
    const [completedOpen, setCompletedOpen] = useState(true);
    const navigate = useNavigate();
    const { setViewEvent, eventData, imageCache, setImageCache, factor, appTheme, windowSize, userRegistrations, priceDisplayMode } = useMCContext();
    const isExpandedView = windowSize.width >= 900;
    const isMobile = !isExpandedView;
    useEffect(() => {
        // Pre-load and cache images
        eventData.forEach((event: any) => {
            // Only fetch if URL exists and image is not already cached
            if (event.eventCardImageURL && !imageCache[event.eventCardImageURL]) {
                fetch(event.eventCardImageURL)
                    .then(response => response.blob())
                    .then(blob => {
                        // Check cache again before creating new object URL
                        if (!imageCache[event.eventCardImageURL]) {
                            const objectURL = URL.createObjectURL(blob);
                            setImageCache((prev: any) => ({
                                ...prev,
                                [event.eventCardImageURL]: objectURL
                            }));
                        }
                    })
                    .catch(error => console.error('Error caching image:', error));
            }
        });

    }, [eventData, imageCache, setImageCache]);

    function formatDate(event: any) {
        const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
        if (new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })) {
            return formatDateForDisplay(event.startDate, opts);
        }
        return formatDateForDisplay(event.startDate, opts) + ' - ' + formatDateForDisplay(event.endDate, opts);
    }

    function formatTime(startingTime: string) {
        if (startingTime) {
            return new Date(`2000-01-01T${startingTime}`).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
        }
        else {
            return '';
        }
    }

    const RegistrationBanner = (passedEvent: any) => {

        const event = passedEvent.event;
        const regNotYetOpen = event.regOpenDate && new Date() < new Date(event.regOpenDate);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = new Date();
        const registrationClosed = registrationCloseExclusive(event.regCloseDate, tz) <= now ||
            new Date(event.startDate) < now;
        const fieldFull = event.playerCap === event.registrationCount;

        const banner = () => {
            if (regNotYetOpen) {
                return (
                    <Box sx={{
                            width: '100%',
                            backgroundColor: appTheme.palette.secondary.main,
                            color: appTheme.palette.secondary.contrastText ?? 'white',
                            padding: 0.5,
                            borderRadius: 1,
                        }}>
                        <Typography variant='caption' sx={{ fontWeight: 500, fontSize: isMobile ? '0.7rem' : undefined }}>
                            Registration Opens {formatDateForDisplay(event.regOpenDate, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                    </Box>
                );
            }
            if (registrationClosed) {
                return (
                    <Box sx={{ width: '100%', backgroundColor: 'rgba(149, 146, 146, 0.4)', padding: 0.5, borderRadius: 1 }}>
                            <Typography variant='caption' sx={{ fontWeight: 500, fontSize: isMobile ? '0.7rem' : undefined }}>
                                Registration Closed
                            </Typography>
                        </Box>)
            }
            if (fieldFull) {
                return (
                    <Box sx={{ width: '100%', backgroundColor: 'rgba(149, 146, 146, 0.4)', padding: 0.5, borderRadius: 1 }}>
                        <Typography variant='caption' sx={{ fontWeight: 500, fontSize: isMobile ? '0.7rem' : undefined }}>
                            Field is Full
                        </Typography>
                    </Box>
                )
            }
            // Registration is open
            return (
                <Box sx={{
                    width: '100%',
                    backgroundColor: appTheme.palette.primary.extraLight,
                    padding: 0.5,
                    borderRadius: 1,
                }}>
                    <Typography variant='caption' sx={{ fontWeight: 500, fontSize: isMobile ? '0.7rem' : undefined }}>
                        Registration Open
                    </Typography>
                </Box>
            );
        }

        return banner();
    }

    // Separate and sort events
    const currentDate = new Date();
    const activeEvents = eventData
        .filter((event: any) => new Date(event.endDate) >= currentDate)
        .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    
    const completedEvents = eventData
        .filter((event: any) => new Date(event.endDate) < currentDate)
        .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    // Apply togglePastEvents filter
    const filteredActiveEvents = activeEvents;
    const filteredCompletedEvents = togglePastEvents ? completedEvents : [];

    const priceFontSize = isMobile ? { fontSize: '0.8rem' } : {};
    const renderPrice = (event: any) => {
        if (!event.eventFee && !event.flightPricing) return null;
        const regQty = event.regQty ?? 1;
        const earlyRegActive = isEarlyRegistrationActive(event) && getEarlyRegDiscountAmount(event) > 0;
        const perRegSuffix = (
            <Typography variant="subtitle2" sx={{ fontWeight: 500, ...priceFontSize }}>
                {getPriceDisplaySuffix(priceDisplayMode)}
            </Typography>
        );

        if (event.flightPricing) {
            const range = getFlightPriceRange(event);
            if (!range) return <Typography variant="subtitle1" sx={priceFontSize}>Price by division/flight</Typography>;
            const discount = getEarlyRegDiscountAmount(event);
            const minWithDiscount = range.min - discount;
            const maxWithDiscount = range.max - discount;
            const scaledRange = scalePriceRangeForDisplay(range, regQty, priceDisplayMode);
            const scaledMinWithDiscount = scalePriceForDisplay(minWithDiscount, regQty, priceDisplayMode);
            const scaledMaxWithDiscount = scalePriceForDisplay(maxWithDiscount, regQty, priceDisplayMode);
            const regularRangeLabel = scaledRange.min === scaledRange.max
                ? `From ${formatDisplayPrice(scaledRange.min)}`
                : `From ${formatDisplayPrice(scaledRange.min)} – ${formatDisplayPrice(scaledRange.max)}`;
            const currentRangeLabel = earlyRegActive
                ? (scaledRange.min === scaledRange.max
                    ? `From ${formatDisplayPrice(scaledMinWithDiscount)}`
                    : `From ${formatDisplayPrice(scaledMinWithDiscount)} – ${formatDisplayPrice(scaledMaxWithDiscount)}`)
                : regularRangeLabel;

            return (
                <EarlyRegPriceDisplay
                    currentPriceLabel={currentRangeLabel}
                    regularPriceLabel={regularRangeLabel}
                    cutoffDate={event.earlyRegCutoffDate}
                    earlyRegActive={earlyRegActive}
                    suffix={perRegSuffix}
                    typographySx={priceFontSize}
                    typographyVariant="subtitle2"
                />
            );
        }

        const regularPriceLabel = formatDisplayPrice(
            scalePriceForDisplay(event.eventFee, regQty, priceDisplayMode)
        );
        const currentPriceLabel = earlyRegActive
            ? formatDisplayPrice(
                scalePriceForDisplay(applyEarlyRegDiscount(event.eventFee, event), regQty, priceDisplayMode)
            )
            : regularPriceLabel;

        return (
            <EarlyRegPriceDisplay
                currentPriceLabel={currentPriceLabel}
                regularPriceLabel={regularPriceLabel}
                cutoffDate={event.earlyRegCutoffDate}
                earlyRegActive={earlyRegActive}
                suffix={perRegSuffix}
                typographySx={priceFontSize}
                typographyVariant="subtitle2"
            />
        );
    };

    const InfoPopperContent = ({ event }: { event: any }) => {
        const loc = event?.courseInfo?.location;
        const boxSx = {
            p: 2,
            borderRadius: 2,
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
        } as const;
        return (
            <Stack direction="column" spacing={2} sx={{ p: 2, minWidth: 260 }}>
                <Box sx={boxSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Course Information</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{event.courseName}</Typography>
                    {loc && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word', mt: 0.5 }}>
                            {[loc.address, loc.city, `${loc.state || ''} ${loc.zip || ''}`.trim()].filter(Boolean).join(', ')}
                        </Typography>
                    )}
                </Box>
                <Box sx={boxSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Event Information</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {event.playFormat} — {event.playType}
                        {event.eventRegion && event.eventState && ` · ${event.eventRegion}, ${event.eventState}`}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.25fr 1.25fr 0.9fr', gap: '8px 24px', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Holes:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.totalHoles}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Rounds:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.totalRounds}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Field:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.playerCap}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Sign Up:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.regType}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Closes:</Typography>
                            <Typography variant="body2" color="text.secondary">{formatDateForDisplay(event.regCloseDate)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>Open:</Typography>
                            <Typography variant="body2" color="text.secondary">{event.playerCap - event.registrationCount}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Stack>
        );
    };

    const renderEventCard = (event: any) => {
        const imageWidth = isExpandedView ? 120 * factor : 100 * factor;
        return (
            <Box key={`box-${event._id}`} sx={{
                flex: isMobile ? '1 1 100%' : `0 1 550px`,
                minWidth: isMobile ? 280 : 550,
                maxWidth: 550,
                boxSizing: 'border-box',
            }}>
                <Paper
                    elevation={2}
                    sx={{
                        width: '100%',
                        padding: 1.5 * factor,
                        border: `1px solid ${appTheme.palette.secondary.main}`,
                        position: 'relative',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                    onClick={() => {
                        setViewEvent(event);
                        navigate(`/events/${event._id}`);
                    }}
                >
                    <Stack direction="column" spacing={1.5}>
                        {/* Header row: Registered mark, Banner, Info button */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minHeight: 28 }}>
                            <Box sx={{ minWidth: 24, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                {userRegistrations.some((r: any) => r.eventID === event._id) ? (
                                    <UserRegistered eventID={event._id} position="inline" />
                                ) : (() => {
                                    const regNotYetOpen = event.regOpenDate && new Date() < new Date(event.regOpenDate);
                                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                    const now = new Date();
                                    const registrationClosed = registrationCloseExclusive(event.regCloseDate, tz) <= now ||
                                        new Date(event.startDate) < now;
                                    const fieldFull = event.playerCap === event.registrationCount;
                                    const registrationOpen = !regNotYetOpen && !registrationClosed && !fieldFull;
                                    return registrationOpen ? (
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="contained"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setViewEvent(event);
                                                navigate(`/events/${event._id}`, { state: { registrationTab: 1 } });
                                            }}
                                        >
                                            Register
                                        </Button>
                                    ) : null;
                                })()}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <RegistrationBanner event={event} />
                            </Box>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPopperAnchorEl(popperEventId === event._id ? null : e.currentTarget);
                                    setPopperEventId(popperEventId === event._id ? null : event._id);
                                }}
                                sx={{ color: appTheme.palette.primary.main, flexShrink: 0 }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        {/* Row: Image left, Info right */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
                            {/* Left: Image */}
                            <Stack direction="column" sx={{ flexShrink: 0, alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        maxWidth: imageWidth,
                                        maxHeight: isExpandedView ? 100 * factor : 80 * factor,
                                        overflow: 'hidden',
                                        borderRadius: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <img
                                        src={event.eventCardImageURL ? (imageCache[event.eventCardImageURL] || event.eventCardImageURL) : QUILTED_FLAG_LOGO}
                                        alt={event.eventName}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: isExpandedView ? 100 * factor : 80 * factor,
                                            width: 'auto',
                                            height: 'auto',
                                            display: 'block',
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                </Box>
                            </Stack>
                            {/* Right: Info */}
                            <Stack direction="column" spacing={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', alignItems: isMobile ? 'center' : 'stretch' }} justifyContent="space-between">
                                <Stack direction="column" spacing={0.5} sx={{ alignItems: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.95rem' : undefined }}>{event.eventName}</Typography>
                                    <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 500, textAlign: isMobile ? 'center' : 'left', overflow: 'hidden', wordBreak: 'break-word', minWidth: 0, fontSize: isMobile ? '0.8rem' : undefined }}>@ {event.courseName}</Typography>
                                    {isMobile ? (
                                        <Stack direction="column" spacing={0.25} alignItems="center">
                                            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>{formatDate(event)}{event.startingTime ? `, ${formatTime(event.startingTime)}` : ''}</Typography>
                                            {renderPrice(event)}
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>{formatDate(event)}{event.startingTime ? `, ${formatTime(event.startingTime)}` : ''}</Typography>
                                            {renderPrice(event)}
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                        {/* Tags row below all other info */}
                        {(new Date(event.endDate) < new Date() || (event.tags && event.tags.length > 0)) && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, justifyContent: 'center' }}>
                                {new Date(event.endDate) < new Date() && (
                                    <Chip label="Past" size="small" sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: 'error.light', color: 'white' }} />
                                )}
                                {event.tags?.map((tag: string, index: number) => (
                                    <Chip key={`${event._id}-tag-${index}`} label={tag} size="small"
                                        sx={{ height: isMobile ? 18 : 20 * factor, fontSize: isMobile ? 11 : 12, backgroundColor: appTheme.palette.primary.light, color: 'white' }} />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            </Box>
        );
    };
    
    return (

            <Stack 
                ref={stackRef as React.RefObject<HTMLDivElement>}
                flex={1} 
                direction={'column'}
                justifyContent={'flex-start'}
                sx={{ 
                    gap: 5 * factor,
                    alignItems: 'center',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    height: '100%',
                    width: '100%',
                    padding: 1,
                    boxSizing: 'border-box',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    }
                }}
            >
                <Stack key="upcoming" width="100%" justifyContent="space-evenly">
                    <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}
                        sx={{ borderBottom: upcomingOpen ? `1px dotted ${appTheme.palette.primary.main}` : 'none' }}>
                        <Typography variant="h6" sx={isMobile ? { fontSize: '0.95rem' } : {}}>Upcoming Events</Typography>
                        <IconButton
                            sx={{ width: 30, height: 30 }}
                            onClick={() => setUpcomingOpen(!upcomingOpen)}>
                            {upcomingOpen ?
                                <ArrowDropUp sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                                :
                                <ArrowDropDown sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                            }
                        </IconButton>
                    </Stack>
                    {upcomingOpen && (
                        <Stack
                            flex={1}
                            direction="row"
                            flexWrap="wrap"
                            sx={{
                                gap: 2 * factor,
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            {filteredActiveEvents.map((event: any) => renderEventCard(event))}
                        </Stack>
                    )}
                </Stack>
                <Divider sx={{ width: '100%'}} />
                <Stack key="completed" width="100%" justifyContent="space-evenly">
                    <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}
                        sx={{ borderBottom: completedOpen ? `1px dotted ${appTheme.palette.primary.main}` : 'none' }}>
                        <Typography variant="h6" sx={isMobile ? { fontSize: '0.95rem' } : {}}>Completed Events</Typography>
                        <IconButton
                            sx={{ width: 30, height: 30 }}
                            onClick={() => setCompletedOpen(!completedOpen)}>
                            {completedOpen ?
                                <ArrowDropUp sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                                :
                                <ArrowDropDown sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                            }
                        </IconButton>
                    </Stack>
                    {completedOpen && (
                        <Stack
                            flex={1}
                            direction="row"
                            flexWrap="wrap"
                            sx={{
                                gap: 2 * factor,
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            {filteredCompletedEvents.map((event: any) => renderEventCard(event))}
                        </Stack>
                    )}
                </Stack>
                <Popover
                    open={!!popperAnchorEl && !!popperEventId}
                    anchorEl={popperAnchorEl}
                    onClose={() => {
                        setPopperAnchorEl(null);
                        setPopperEventId(null);
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { elevation: 4 } }}
                    sx={{ zIndex: 1300 }}
                    disableScrollLock
                >
                    {popperEventId && (() => {
                        const event = [...filteredActiveEvents, ...filteredCompletedEvents].find((e: any) => e._id === popperEventId);
                        return event ? <InfoPopperContent event={event} /> : null;
                    })()}
                </Popover>
            </Stack>
    )
}
